import Vue from "vue";
import i18nService from "@/core/services/i18n.service";
import axios from "axios";

/**
 * Service to call HTTP request via Axios
 */

export default class ApiService {
  name = "";
  url = "";
  token = "";
  headers = {};
  axiosInstance = undefined;
  enableLogger = false;

  constructor(name, url, token) {
    this.name = name;
    this.url = url;
    this.token = token;

    this.axiosInstance = axios.create({
      baseURL: this.url,
      headers: {
        ...this.defaultHeaders
      }
    });

    this.axiosInstance.interceptors.request.use(request => {
      if (this.enableLogger) {
        console.log(
          `[${this.name}]: Starting Request`,
          JSON.stringify(request, null, 2)
        );
      }
      return request;
    });

    this.axiosInstance.interceptors.response.use(response => {
      if (this.enableLogger) {
        console.log(
          `[${this.name}]: Response`,
          JSON.stringify(response, null, 2)
        );
      }
      return response;
    });
  }

  get defaultHeaders() {
    const cookieClientId = window.KTCookie.getCookie(
      "_HAPI_OVERRIDE_CLIENT_ID"
    );
    const cookieApiToken = window.KTCookie.getCookie(
      "_HAPI_OVERRIDE_API_TOKEN"
    );
    return {
      "X-Customer-Id": cookieClientId || Vue.prototype.$auth.user.sub,
      "Accept-Language": i18nService.getActiveLanguage(),
      "X-AUTH-TOKEN": cookieApiToken || this.token
    };
  }

  query(resource, params) {
    return this.axiosInstance.get(resource, { params: params }).catch(error => {
      throw new Error(`[${this.name}] query error:  ${error}`);
    });
  }

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  get(resource, slug = "") {
    let urlPath = resource;
    if (resource && slug && typeof slug !== "object") {
      // remove slash from end of `resource` and start of `slug` before joining
      const cleanResource = resource.toString().replace(/\/$/, "");
      const cleanSlug = slug.toString().replace(/^\//, "");
      urlPath = `${cleanResource}/${cleanSlug}`;
    }

    return this.axiosInstance.get(`${urlPath}`).catch(error => {
      console.log(error);
      throw new Error(`[${this.name}] get error:  ${error}`);
    });
  }

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params) {
    return this.axiosInstance.post(`${resource}`, params);
  }

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return this.axiosInstance.put(`${resource}/${slug}`, params);
  }

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params) {
    return this.axiosInstance.put(`${resource}`, params);
  }

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    return this.axiosInstance.delete(resource).catch(error => {
      // console.log(error);
      throw new Error(`[${this.name}] delete error:  ${error}`);
    });
  }
}
